import { useState, useEffect, useCallback, useRef } from 'react';
import { socketService } from '../../services/socket.service';
import './index.css';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';
import MailIcon from '@mui/icons-material/Mail';
import axiosInstance from '../../helpers/axios';
import notificationSound from './notification.wav';
import Modal from '@mui/material/Modal';
import Button from '../shared/Button/Button';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useCurrentUser } from '../../hooks/useCurrentUser';

export default function Notifications() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [notifications, setNotifications] = useState([]);
  const [futureReminders, setFutureReminders] = useState([]);
  const [upcomingNotifications, setUpcomingNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadNotificationsList, setUnreadNotificationsList] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedDateTime, setSelectedDateTime] = useState(new Date());
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState('inbox');
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track if data has been loaded
  const audioRef = useRef(new Audio(notificationSound));
  const decoded = useCurrentUser();
  const user = null;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const moreMenuOpen = Boolean(moreMenuAnchorEl);

  const isReminderDue = (dateTimeString) => {
    if (!dateTimeString || dateTimeString === "0000-00-00 00:00:00") return true;
    const [datePart, timePart] = dateTimeString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);

    const reminderDate = new Date(year, month - 1, day, hour, minute, second);
    const now = new Date();

    return reminderDate <= now;
  };

  const isPotentialReminder = (notification) => {
    return notification.dateTime && notification.dateTime !== "0000-00-00 00:00:00";
  };

  // Function to move due reminders from futureReminders to notifications
  const moveDueReminders = useCallback(() => {
    setFutureReminders(prevFuture => {
      const dueReminders = [];
      const stillFutureReminders = [];

      prevFuture.forEach(reminder => {
        if (isReminderDue(reminder.dateTime)) {
          dueReminders.push(reminder);
        } else {
          stillFutureReminders.push(reminder);
        }
      });
      if (dueReminders.length > 0) {
        setNotifications(prevNotifications => {
          const updatedNotifications = [...dueReminders, ...prevNotifications];
          const filteredUpdatedNotifications = updatedNotifications.filter(
            item => item.createdBy !== "System"
          );
          const unreadCount = filteredUpdatedNotifications.filter(n => n.status === "0").length;
          setUnreadCount(unreadCount);
          return filteredUpdatedNotifications;
        });

        // Remove due reminders from the upcomingNotifications state
        setUpcomingNotifications(prevUpcoming =>
          prevUpcoming.filter(upcoming =>
            !dueReminders.some(due => due.id === upcoming.id)
          )
        );
      }

      return stillFutureReminders;
    });
  }, []);

  // Modified fetchNotifications - now called on demand
  const fetchNotifications = async () => {
    if (isLoading) return; // Prevent multiple simultaneous calls
    
    setIsLoading(true);
    try {
      // Fetch current notifications
      const response = await axiosInstance.get(`/dashboard-notifications/all-notifications?userId=${decoded?.user_id}`, {
        headers: {
        }
      });

      // Fetch upcoming notifications
      const upcomingResponse = await axiosInstance.get(`/dashboard-notifications/all-notifications?userId=${decoded?.user_id}&isSent=0`, {
        headers: {
        }
      });

      if (response.data.message === "Successful") {
        const allNotifications = response.data.data.map(notification => ({
          id: notification.id,
          message: notification.notes,
          timestamp: notification.dateTime,
          read: notification.status === "1",
          type: isPotentialReminder(notification) ? 'reminder' : 'notification',
          createdBy: notification.createdBy,
          status: notification.status,
          dateTime: notification.dateTime,
          isSent: notification.isSent,
          role: notification.role
        }));

        const currentNotifications = [];
        const futureRemindersList = [];

        // Calculate date 5 days ago
        const fiveDaysAgo = new Date();
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

        allNotifications.forEach(notification => {
          const notificationDate = new Date(notification.timestamp);
          if (notification.type === 'reminder' && !isReminderDue(notification.dateTime)) {
            futureRemindersList.push(notification);
          } else if (notificationDate >= fiveDaysAgo) {
            currentNotifications.push(notification);
          }
        });

        setNotifications(currentNotifications);
        setFutureReminders(futureRemindersList);

        const unreadNotifications = currentNotifications.filter(n => n.status === "0").length;
        setUnreadCount(unreadNotifications);
      }

      if (upcomingResponse.data.message === "Successful") {
        const upcoming = upcomingResponse.data.data.map(notification => ({
          id: notification.id,
          message: notification.notes,
          timestamp: notification.dateTime,
          type: 'upcoming',
          createdBy: notification.createdBy,
          dateTime: notification.dateTime
        }));
        setUpcomingNotifications(upcoming);
      }

      setDataLoaded(true); // Mark data as loaded
    } catch (error) {
      setAlert({
        open: true,
        message: 'Error loading notifications',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Modified fetchSystemUnreadNotifications - now called on demand
  const fetchSystemUnreadNotifications = async () => {
    try {
      const response = await axiosInstance.get('/dashboard-notifications/all-notifications?status=0&isSent=1', {
        headers: {
        }
      });
      if (response.data.message === "Successful") {
        setUnreadNotificationsList(response.data.data.map(notification => ({
          id: notification.id,
          message: notification.notes,
          timestamp: notification.dateTime,
          read: notification.status === "1",
          type: isPotentialReminder(notification) ? 'reminder' : 'notification',
          createdBy: notification.createdBy,
          status: notification.status,
          dateTime: notification.dateTime,
          isSent: notification.isSent,
          role: notification.role
        })));
      }
    } catch (error) {
      console.error('Error fetching system unread notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.post('/dashboard-notifications/mark-as-read', { id: notificationId }, {
        headers: {
        }
      });
      setNotifications(prev => {
        const updatedNotifications = prev.map(n =>
          n.id === notificationId ? { ...n, read: true, status: "1" } : n
        );
        const unreadCount = updatedNotifications.filter(n => n.status === "0").length;
        setUnreadCount(unreadCount);
        return updatedNotifications;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Combined handler for both notifications and reminders
  const handleIncomingData = useCallback((data) => {

    // Handle reminder status updates
    if (data.type === 'reminderStatus') {
      setNotifications(prev =>
        prev.map(n =>
          n.reminderId === data.reminderId
            ? { ...n, status: data.status }
            : n
        )
      );
      return;
    }

    // Process new notification/reminder
    const potentialReminder = data.type === 'reminder' || isPotentialReminder(data);

    const newNotification = {
      id: data.id || Date.now(),
      reminderId: data.reminderId,
      message: data.message || data.notes || 'New notification',
      timestamp: data.timestamp || new Date().toLocaleString(),
      read: false,
      type: potentialReminder ? 'reminder' : 'notification',
      priority: data.priority || 'normal',
      details: data.details || {},
      status: data.status || "0",
      dateTime: data.dateTime || new Date().toISOString().slice(0, 19).replace('T', ' '),
      createdBy: data.createdBy || 'System',
      role: data.role
    };


    // Check if it's a future reminder
    if (potentialReminder && !isReminderDue(newNotification.dateTime)) {
      setFutureReminders(prev => [newNotification, ...prev]);
      // Also add to upcomingNotifications if it's a future reminder
      setUpcomingNotifications(prev => [newNotification, ...prev]);
      return;
    }

    // Add to current notifications if it's due now or not a reminder,
    // and ensure it's not already in the notifications list (to prevent duplicates from socket)
    setNotifications(prev => {
      // Check if notification with the same ID already exists
      if (prev.some(n => n.id === newNotification.id)) {
        return prev;
      }

      const updatedNotifications = [newNotification, ...prev];
      const unreadCount = updatedNotifications.filter(n => n.status === "0").length;
      setUnreadCount(unreadCount);

      // Play notification sound for new notifications
      if (newNotification.status === "0") {
        audioRef.current.play().catch(error => {
        });
      }

      return updatedNotifications;
    });
  }, []);

  // Modified useEffect - only setup socket, no API calls on mount
  useEffect(() => {
    if (user) {
      socketService.connect(user);

      // Register handlers for BOTH notification and reminder events
      socketService.addNotificationHandler(handleIncomingData);
      socketService.addReminderHandler(handleIncomingData);

      return () => {
        socketService.removeNotificationHandler(handleIncomingData);
        socketService.removeReminderHandler(handleIncomingData);
        socketService.disconnect();
      };
    }
  }, [user, handleIncomingData]);

  useEffect(() => {
    const reminderCheckInterval = setInterval(() => {
      moveDueReminders();
    }, 60 * 1000);

    return () => {
      clearInterval(reminderCheckInterval);
    };
  }, [moveDueReminders]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Load system notifications when switching to 'all' tab
    if (newValue === 'all' && decoded?.role === 'crm' && unreadNotificationsList.length === 0) {
      fetchSystemUnreadNotifications();
    }
  };

  // Modified handleMenuClick - now fetches data when clicked
  const handleMenuClick = async (event) => {
    setAnchorEl(event.currentTarget);
    
    // Fetch data only when notification icon is clicked
    if (!dataLoaded || !open) {
      await fetchNotifications();
      if (decoded?.role === 'crm') {
        await fetchSystemUnreadNotifications();
      }
    }
    
    moveDueReminders();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleCloseMoreMenu();
  };

  const handleOpenMoreMenu = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    handleCloseMoreMenu();
    try {
      const unreadIds = unreadNotificationsList.map(item => item.id.toString());
      await axiosInstance.post('/dashboard-notifications/mark-as-read-all', {
        userId: decoded.user_id,
        id: unreadIds
      }, {
        headers: {
        }
      });

      // Update local state after successful API call
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, status: "1" }))
      );
      setUnreadCount(0);
      setUnreadNotificationsList([]);

      setAlert({
        open: true,
        message: 'All notifications marked as read',
        severity: 'success'
      });
    } catch (error) {
      setAlert({
        open: true,
        message: 'Error marking notifications as read',
        severity: 'error'
      });
    }
  };

  const handleReadUnread = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  const getNotificationStyle = (type, priority, status) => {
    const baseStyle = {
      backgroundColor: '#FFF',
      opacity: status === "1" ? 0.7 : 1
    };

    if (type === 'reminder' && priority === 'high') {
      return {
        ...baseStyle,
        backgroundColor: '#FFEBEE',
      };
    }

    return baseStyle;
  };

  const handleCreateNotification = async () => {
    try {
      const year = selectedDateTime.getFullYear();
      const month = ('0' + (selectedDateTime.getMonth() + 1)).slice(-2); // Month is 0-indexed
      const day = ('0' + selectedDateTime.getDate()).slice(-2);
      const hours = ('0' + selectedDateTime.getHours()).slice(-2);
      const minutes = ('0' + selectedDateTime.getMinutes()).slice(-2);
      const seconds = '00'; // Set seconds to 00
      const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      const response = await axiosInstance.post('/dashboard-notifications/create', {
        createdBy: decoded.user_id,
        notes: notes,
        dateTime: formattedDateTime,
        status: "0"
      }, {
        headers: {
        }
      });

      if (response.data.message === "Successful") {
        setAlert({
          open: true,
          message: 'Created successfully',
          severity: 'success'
        });
        setCreateModalOpen(false);
        setNotes('');
        setSelectedDateTime(new Date());
        
        // Refresh data after creating notification
        setDataLoaded(false);
        await fetchNotifications();
        if (decoded?.role === 'crm') {
          await fetchSystemUnreadNotifications();
        }
      }
    } catch (error) {
      setAlert({
        open: true,
        message: 'Error creating notification',
        severity: 'error'
      });
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleDeleteUpcoming = (notification) => {
    setNotificationToDelete(notification);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!notificationToDelete || !notificationToDelete.id) return;
    try {
      await axiosInstance.post('/dashboard-notifications/delete', { id: notificationToDelete.id }, {
        headers: {
        }
      });

      setUpcomingNotifications(prev => prev.filter(item => item.id !== notificationToDelete.id));
      setDeleteModalOpen(false);
      setNotificationToDelete(null);
      setAlert({
        open: true,
        message: 'Notification deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      setAlert({
        open: true,
        message: 'Error deleting notification',
        severity: 'error'
      });
      setDeleteModalOpen(false);
      setNotificationToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setNotificationToDelete(null);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="notification-bell-button"
        aria-label="Notifications"
        onClick={handleMenuClick}
        style={{
          width: 36,
          height: 36,
          padding: 0,
          borderRadius: "10px",
          background: "#fff5f5",
          border: "1.5px solid #f3e8e8",
          color: "#D32028",
        }}
      >
        <Badge
          badgeContent={unreadCount}
          sx={{
            '& .MuiBadge-badge': {
              background: "#D32028",
              color: "#fff",
              fontSize: "10px",
              minWidth: 16,
              height: 16,
              padding: "0 4px",
            },
          }}
        >
          <NotificationsIcon fontSize="small" />
        </Badge>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px' }}>
          <Typography variant="h6">
            Notifications
          </Typography>
          <div>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setCreateModalOpen(true);
              }}
              style={{ marginLeft: '8px' }}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleOpenMoreMenu}
              style={{ marginLeft: '8px' }}
              aria-label="more options"
            >
              <MoreVertIcon />
            </IconButton>
          </div>

        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          {decoded?.role === 'crm' && (
            <Tab label={`TEAM (${unreadNotificationsList.filter(item => item.role === 'cra').length})`} value="all" />
          )}
          <Tab label={`Inbox (${unreadCount} unread)`} value="inbox" />
          <Tab label={`Upcoming (${upcomingNotifications.length})`} value="upcoming" />
        </Tabs>

        {/* Loading indicator */}
        {isLoading && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Loading notifications...
            </Typography>
          </div>
        )}

        {/* Tab Content */}
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {activeTab === 'all' && decoded?.role === 'crm' && !isLoading && (
            <>
              {/* Unread Notifications Section */}
              {unreadNotificationsList
                .filter(item => item.role === 'cra')
                .map((item) => (
                  <MenuItem
                    key={item.id}
                    onClick={() => handleReadUnread(item)}
                    style={{
                      ...getNotificationStyle(item.type, item.priority, item.status),
                      lineHeight: '1.2em',
                      display: 'flex',
                      flexDirection: 'row',
                      borderBottom: '1px solid #DBDBDB',
                      padding: '10px',
                      fontSize: '14px',
                      whiteSpace: 'normal',
                      minWidth: '300px'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Typography variant="body2" style={{ paddingTop: '5px', margin: 0, fontWeight: 'bold' }}>
                        {item.message}
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', gap: '10px' }}>
                        <Typography variant="caption" style={{ color: '#666' }}>
                          {new Date(item.timestamp).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#666', marginLeft: '10px' }}>
                          Created by: {item.createdBy}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(item.id);
                          }}
                          sx={{
                            padding: '2px',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                          }}
                        >
                          {item.read ? (
                            <DoneAllIcon fontSize="small" sx={{ color: '#1976d2' }} />
                          ) : (
                            <MailIcon fontSize="small" sx={{ color: '#666' }} />
                          )}
                        </IconButton>
                      </div>
                    </div>
                  </MenuItem>
                ))}
              {unreadNotificationsList.length === 0 && (
                <MenuItem>
                  <Typography variant="body2">No unread notifications</Typography>
                </MenuItem>
              )}
            </>
          )}

          {activeTab === 'inbox' && !isLoading && (
            <>
              {/* Current Notifications (Inbox) Section */}
              {notifications.map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={() => handleReadUnread(item)}
                  style={{
                    ...getNotificationStyle(item.type, item.priority, item.status),
                    lineHeight: '1.2em',
                    display: 'flex',
                    flexDirection: 'row',
                    borderBottom: '1px solid #DBDBDB',
                    padding: '10px',
                    fontSize: '14px',
                    whiteSpace: 'normal',
                    minWidth: '300px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Typography variant="body2" style={{ paddingTop: '5px', margin: 0, fontWeight: 'bold' }}>
                      {item.message}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', gap: '10px' }}>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        {new Date(item.timestamp).toLocaleString('en-GB')}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(item.id);
                        }}
                        sx={{
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}
                      >
                        {item.read ? (
                          <DoneAllIcon fontSize="small" sx={{ color: '#1976d2' }} />
                        ) : (
                          <MailIcon fontSize="small" sx={{ color: '#666' }} />
                        )}
                      </IconButton>
                    </div>
                  </div>
                </MenuItem>
              ))}
              {notifications.length === 0 && (
                <MenuItem>
                  <Typography variant="body2">No inbox notifications</Typography>
                </MenuItem>
              )}
            </>
          )}

          {activeTab === 'upcoming' && !isLoading && (
            <>
              {/* Upcoming Notifications Section */}
              {upcomingNotifications.map((item) => (
                <MenuItem
                  key={item.id}
                  style={{
                    lineHeight: '1.2em',
                    display: 'flex',
                    flexDirection: 'row',
                    borderBottom: '1px solid #DBDBDB',
                    padding: '10px',
                    fontSize: '14px',
                    whiteSpace: 'normal',
                    minWidth: '300px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Typography variant="body2" style={{ paddingTop: '5px', margin: 0, fontWeight: 'bold' }}>
                      {item.message}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', gap: '10px' }}>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        Scheduled for: {new Date(item.dateTime).toLocaleString('en-GB')}
                      </Typography>
                    </div>
                  </div>
                  {/* Delete Icon */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteUpcoming(item);
                    }}
                    aria-label="delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </MenuItem>
              ))}
              {upcomingNotifications.length === 0 && (
                <MenuItem>
                  <Typography variant="body2">No upcoming notifications</Typography>
                </MenuItem>
              )}
            </>
          )}
        </div>

        {/* Mark All As Read Menu */}
        <Menu
          anchorEl={moreMenuAnchorEl}
          open={moreMenuOpen}
          onClose={handleCloseMoreMenu}
        >
          <MenuItem onClick={handleMarkAllAsRead}>
            Mark all as Read
          </MenuItem>
        </Menu>

      </Menu>

      <Modal
        open={createModalOpen}
        aria-labelledby="create-notification-modal"
      >
        <div style={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          backgroundColor: 'white',
          boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)',
          padding: '32px',
          borderRadius: '8px',
        }}>

          <Typography variant="h6" component="h2" gutterBottom>
            Create
          </Typography>
          <div style={{ marginTop: '10px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Date and Time"
                value={selectedDateTime}
                onChange={setSelectedDateTime}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                sx={{ width: '100%', marginTop: '1.5em' }}
              />
            </LocalizationProvider>
          </div>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            sx={{ width: '100%', marginTop: '1.5em' }}
          />
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreateNotification}
              disabled={!notes.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-confirmation-modal"
      >
        <div style={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          backgroundColor: 'white',
          boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)',
          padding: '32px',
          borderRadius: '8px',
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Confirm Deletion
          </Typography>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete the upcoming notification scheduled for:
          </Typography>
          {notificationToDelete && notificationToDelete.dateTime && (
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              {new Date(notificationToDelete.dateTime).toLocaleString()}
            </Typography>
          )}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={cancelDelete}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}